import React from 'react';
import { Button, IconButton, Fab, Tooltip, ButtonProps, IconButtonProps, FabProps } from '@mui/material';
import { usePermissions } from '../contexts/PermissionContext';

interface PermissionButtonProps {
  moduleName: string;
  operation: 'view' | 'add' | 'edit' | 'delete';
  miniModuleName?: string;
  children: React.ReactNode;
  variant?: 'text' | 'outlined' | 'contained';
  component?: 'Button' | 'IconButton' | 'Fab';
  tooltipTitle?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  [key: string]: any;
}

/**
 * Permission-aware button component
 * - Shows button if user has permission
 * - Disables button if user lacks permission
 * - Shows tooltip explaining why button is disabled
 * - Zero performance overhead (uses cached permissions)
 */
const PermissionButton: React.FC<PermissionButtonProps> = ({
  moduleName,
  operation,
  miniModuleName,
  children,
  variant = 'contained',
  component = 'Button',
  tooltipTitle,
  disabled = false,
  ...props
}) => {
  const { hasPermission, loading } = usePermissions();

  // Fast permission check (O(1) from cache)
  const hasAccess = hasPermission(moduleName, operation, miniModuleName);
  const isDisabled = disabled || !hasAccess;

  const disabledStyle = {
    opacity: 0.5,
    pointerEvents: 'none' as const,
    cursor: 'not-allowed',
    filter: 'grayscale(0.8)',
  };

  const operationLabels: Record<string, string> = {
    add: 'ajouter',
    edit: 'modifier',
    delete: 'supprimer',
    view: 'consulter',
  };

  const getTooltipTitle = (): string => {
    if (tooltipTitle) return tooltipTitle;
    if (!hasAccess && !loading) {
      const target = miniModuleName ? `${moduleName} > ${miniModuleName}` : moduleName;
      const operationLabel = operationLabels[operation] || operation;
      return `Vous n'avez pas l'autorisation de ${operationLabel} sur ${target}`;
    }
    return '';
  };

  const buttonProps = {
    ...props,
    disabled: isDisabled,
    style: {
      ...props.style,
      ...(isDisabled ? disabledStyle : {}),
    },
  };

  const renderButton = () => {
    switch (component) {
      case 'IconButton':
        return <IconButton {...(buttonProps as IconButtonProps)}>{children}</IconButton>;
      case 'Fab':
        return <Fab {...(buttonProps as FabProps)}>{children}</Fab>;
      default:
        return (
          <Button variant={variant} {...(buttonProps as ButtonProps)}>
            {children}
          </Button>
        );
    }
  };

  const button = renderButton();
  const tooltipContent = getTooltipTitle();

  if (tooltipContent) {
    return (
      <Tooltip title={tooltipContent} arrow>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};

export default PermissionButton;
